/**
 * Workplan — auto-generated from license graph.
 * From FRONTEND_BUILD_SPEC §3.4: Phases, streams, parallel brackets, critical path.
 */
import { useEffect, useState } from "react";
import { CheckCircle, Circle, AlertTriangle, Clock, MinusCircle, HelpCircle } from "lucide-react";
import { AIInsight } from "../shared/AIInsight.tsx";
import { api } from "../../lib/api.ts";

interface License {
  license_type_id: string;
  license_name: string;
  issuing_authority: string;
  status: string;
  statutory_timeline_days: number | null;
  prerequisite_license_ids: string;
  fields_total: number;
  fields_complete: number;
  docs_total: number;
  docs_complete: number;
  applicability?: string | null;
  applicability_reason?: string | null;
  managed_by?: string | null;
  verification_class?: string | null;
}

interface WorkplanViewProps {
  licenses: License[];
  projectId: string;
  countryCode: string;
}

interface Phase {
  label: string;
  description: string;
  monthRange: string;
  streams: Stream[];
  unlockText?: string;
}

interface Stream {
  label: string;
  isCriticalPath: boolean;
  tasks: Task[];
}

interface Task {
  licenseId: string;
  name: string;
  authority: string;
  status: string;
  durationMonths: number;
  isBlocked: boolean;
  isUnconfirmed: boolean;
  unconfirmedReason?: string | null;
  fieldsComplete: number;
  fieldsTotal: number;
  docsComplete: number;
  docsTotal: number;
  /** D7 badge: row not emitted by the verified corpus pipeline. */
  notCorpusVerified: boolean;
  /** S4 chip: date the latest regulatory update added this row (null = pre-existing). */
  addedByUpdate: string | null;
}

export function WorkplanView({ licenses, projectId, countryCode }: WorkplanViewProps) {
  // Conditional applicability: requirements that don't apply to THIS project
  // are shown in their own section with the reason — never silently hidden.
  // Corpus-RETIRED licence types (retired_reason set; superseded by a named
  // successor) join the same section unless the row is in flight — a project
  // created before a dataset apply keeps its old rows, and they must not
  // render as live workplan items alongside their replacements.
  const inFlight = (l: any) => !["not_started", "preparing", "blocked"].includes(l.status);
  const notApplicable = licenses.filter(l => (l.applicability === "not_applicable" || (l as any).retired_reason) && !inFlight(l));
  const activeLicenses = licenses.filter(l => !notApplicable.includes(l));
  const [showNA, setShowNA] = useState(false);

  // S4 chips: which rows the latest regulatory update added, from the diff
  // the applier recorded at apply time (no dataset yet ⇒ no chips).
  const [regStatus, setRegStatus] = useState<Awaited<ReturnType<typeof api.getRegulatoryStatus>> | null>(null);
  useEffect(() => {
    api.getRegulatoryStatus(projectId).then(setRegStatus).catch(() => {});
  }, [projectId]);
  const addedIds = new Set(regStatus?.status?.diff?.added ?? []);
  const appliedDate = regStatus?.status?.applied_at?.slice(0, 10) ?? null;

  const phases = generatePhases(activeLicenses, countryCode, addedIds, appliedDate);

  if (licenses.length === 0) {
    return <p className="text-[16px] text-text-secondary py-4">No licenses to generate workplan from.</p>;
  }

  const totalTasks = phases.reduce((s, p) => s + p.streams.reduce((ss, st) => ss + st.tasks.length, 0), 0);
  const completedTasks = phases.reduce((s, p) => s + p.streams.reduce((ss, st) =>
    ss + st.tasks.filter(t => t.status === "approved").length, 0), 0);

  return (
    <div className="space-y-1">
      {/* Summary */}
      <div className="flex items-center gap-4 mb-4 text-[14px] text-text-secondary">
        <span>{completedTasks}/{totalTasks} tasks complete</span>
        <span>·</span>
        <span>{phases.length} phases</span>
      </div>

      {/* AI insight */}
      <div className="mb-4">
        <AIInsight
          text={getCriticalPathInsight(phases)}
        />
      </div>

      {/* Phases */}
      {phases.map((phase, pi) => (
        <div key={phase.label}>
          {/* Phase header */}
          <div className="flex items-center gap-2 mb-3 mt-2">
            <h3 className="text-[13px] font-medium text-text-tertiary uppercase tracking-wider">
              {phase.label}
            </h3>
            <span className="text-[12px] text-text-tertiary">{phase.monthRange}</span>
            {phase.description && (
              <span className="text-[12px] text-text-tertiary ml-auto">{phase.description}</span>
            )}
          </div>

          {/* Streams — fork/merge tree for parallel, plain for single */}
          {phase.streams.length > 1 ? (
            <div className="ml-4 mb-3">
              {/* Streams with branch connectors */}
              <div className="border-l-2 border-border-secondary">
                {phase.streams.map((stream) => (
                  <div key={stream.label} className="py-1">
                    {/* Critical path badge (only if flagged) */}
                    {stream.isCriticalPath && (
                      <div className="ml-4 mb-1">
                        <span className="text-[11px] font-medium px-1.5 py-0.5 bg-danger-light text-danger rounded-full">
                          Critical path
                        </span>
                      </div>
                    )}
                    {/* Task cards with connectors */}
                    <div className="space-y-1">
                      {stream.tasks.map(task => (
                        <div key={task.licenseId} className="relative pl-4">
                          {/* Horizontal connector to card border */}
                          <div className="absolute left-0 top-1/2 w-4 border-t-2 border-border-secondary" />
                          {/* Dot on the trunk line */}
                          <div className="absolute left-[-5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-border-secondary border-2 border-surface" />
                          <TaskRow task={task} projectId={projectId} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Unlock text — terminates the branch line */}
              {phase.unlockText && (
                <div className="relative pl-4 pt-2 pb-1">
                  <div className="absolute left-0 top-0 h-1/2 border-l-2 border-border-secondary" />
                  <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-text-tertiary" />
                  <span className="ml-2 text-[12px] text-text-tertiary">{phase.unlockText}</span>
                </div>
              )}
            </div>
          ) : (
            phase.streams.map(stream => (
              <div key={stream.label} className="mb-2">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[13px] font-medium text-text-secondary">{stream.label}</span>
                  {stream.isCriticalPath && (
                    <span className="text-[11px] font-medium px-1.5 py-0.5 bg-danger-light text-danger rounded-full">
                      Critical path
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  {stream.tasks.map(task => (
                    <TaskRow key={task.licenseId} task={task} projectId={projectId} />
                  ))}
                </div>
              </div>
            ))
          )}

          {/* Unlock text for single-stream phases */}
          {phase.streams.length <= 1 && phase.unlockText && (
            <div className="relative py-2 pl-4">
              <div className="absolute left-0 top-0 h-1/2 border-l-2 border-border-secondary" />
              <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-text-tertiary" />
              <span className="ml-2 text-[12px] text-text-tertiary">{phase.unlockText}</span>
            </div>
          )}
        </div>
      ))}

      {/* Not required for this project — shown with reasons, never hidden */}
      {notApplicable.length > 0 && (
        <div className="pt-3 mt-2 border-t border-border">
          <button
            onClick={() => setShowNA(!showNA)}
            className="text-[13px] font-medium text-text-tertiary uppercase tracking-wider hover:text-text-secondary"
          >
            {showNA ? "▾" : "▸"} Not required for this project ({notApplicable.length})
          </button>
          {showNA && (
            <div className="mt-2 space-y-1">
              {notApplicable.map(l => (
                <div key={l.license_type_id} className="flex items-start gap-2.5 px-3 py-2 bg-surface-secondary border border-border rounded-xl opacity-70">
                  <MinusCircle size={14} className="text-text-tertiary flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-[14px] text-text-secondary truncate">{l.license_name}</div>
                    <div className="text-[12px] text-text-tertiary">
                      {l.applicability_reason || "Does not apply to this project's profile."}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function TaskRow({ task, projectId }: { task: Task; projectId: string }) {
  const statusIcon = task.status === "approved" ? (
    <CheckCircle size={14} className="text-brand" />
  ) : task.isBlocked ? (
    <AlertTriangle size={14} className="text-danger" />
  ) : task.status === "preparing" || task.status === "submitted" ? (
    <Clock size={14} className="text-warning-text" />
  ) : (
    <Circle size={14} className="text-text-tertiary" />
  );

  return (
    <a
      href={`/projects/${projectId}/licenses/${task.licenseId}`}
      className={`flex items-center gap-2.5 px-3 py-2 bg-surface border border-border rounded-xl card-interactive hover:border-border-secondary no-underline text-text-primary ${
        task.isBlocked ? "opacity-50" : ""
      }`}
    >
      {statusIcon}
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium truncate">
          {task.name}
          {task.addedByUpdate && (
            <span
              className="ml-2 inline-block align-middle px-1.5 py-0.5 rounded-md bg-brand/10 text-brand text-[11px] font-medium"
              title={`This requirement was added by the regulatory update applied ${task.addedByUpdate}.`}
            >
              Added by regulatory update {task.addedByUpdate}
            </span>
          )}
          {task.notCorpusVerified && (
            <span
              className="ml-2 inline-block align-middle px-1.5 py-0.5 rounded-md border border-border text-text-tertiary text-[11px]"
              title="Hand-seeded before the verified corpus pipeline; not yet re-verified against the original legal text."
            >
              Not corpus-verified
            </span>
          )}
        </div>
        <div className="text-[12px] text-text-tertiary">
          {task.authority} · ~{task.durationMonths}mo
          {(task.fieldsTotal > 0 || task.docsTotal > 0) && (
            <span className="ml-2">
              {task.fieldsTotal > 0 && <span>{task.fieldsComplete}/{task.fieldsTotal} fields</span>}
              {task.fieldsTotal > 0 && task.docsTotal > 0 && <span> · </span>}
              {task.docsTotal > 0 && <span>{task.docsComplete}/{task.docsTotal} docs</span>}
            </span>
          )}
        </div>
      </div>
      {task.isBlocked && (
        <span className="text-[12px] text-danger font-medium">Blocked</span>
      )}
      {task.isUnconfirmed && !task.isBlocked && (
        <span
          className="flex items-center gap-1 text-[12px] text-warning-text font-medium"
          title={task.unconfirmedReason || "Confirm whether this applies to your project"}
        >
          <HelpCircle size={12} /> Confirm applies
        </span>
      )}
    </a>
  );
}

// ─── Workplan generation logic ───

function generatePhases(licenses: License[], countryCode: string, recentlyAdded: Set<string> = new Set(), appliedDate: string | null = null): Phase[] {
  const parsed = licenses.map(l => ({
    ...l,
    prereqs: tryParseJSON(l.prerequisite_license_ids, []) as string[],
    durationMonths: l.statutory_timeline_days ? Math.max(1, Math.round(l.statutory_timeline_days / 30)) : 3,
  }));

  const licMap = new Map(parsed.map(l => [l.license_type_id, l]));

  function isBlocked(l: typeof parsed[0]) {
    return l.prereqs.some(preId => {
      const pre = licMap.get(preId);
      return !pre || pre.status !== "approved";
    });
  }

  // Phase definitions live in src/lib/workplan-phases.ts — ONE catalogue shared
  // with the dashboard List view, plus an explicit unphased remainder so a new
  // corpus id can never silently vanish from this Timeline again (before
  // 2026-07-15 an id in no phase array was filtered out of every phase and the
  // only other section renders not_applicable rows — applicable-but-unknown
  // ids simply disappeared).
  const { phases: phaseDefs, other } = partitionByPhase(countryCode, parsed);
  const allDefs = other.length > 0
    ? [...phaseDefs, {
        label: UNPHASED_LABEL,
        ids: other.map(l => l.license_type_id),
        desc: "Real requirements the phase map has not placed yet — never hidden",
        unlock: undefined as string | undefined,
        licenses: other,
      }]
    : phaseDefs;

  const phases = allDefs.map(pc => {
    const phaseLicenses = pc.licenses;
    if (phaseLicenses.length === 0) return null;

    // Group into streams by related licenses
    const streams: Stream[] = [];
    const used = new Set<string>();

    // Critical path: EIA and grid are typically longest
    const criticalIds = ["KE-L02", "KE-L03", "NG-L03", "ng-persons-wishing-to-connect-to-and-use-the-network-must-acced", "ZA-L02", "ZA-L06", "GH-L02", "GH-L03", "NA-L02", "NA-L03", "ZM-L02", "ZM-L03"];

    for (const lic of phaseLicenses) {
      if (used.has(lic.license_type_id)) continue;
      used.add(lic.license_type_id);

      const isCritical = criticalIds.includes(lic.license_type_id);

      // Find related licenses in same phase (share prerequisites)
      const related = phaseLicenses.filter(l =>
        !used.has(l.license_type_id) &&
        l.prereqs.some(p => lic.prereqs.includes(p))
      );

      const streamLicenses = [lic, ...related];
      related.forEach(r => used.add(r.license_type_id));

      streams.push({
        label: streamLicenses.length === 1
          ? lic.license_name
          : `${lic.license_name}${related.length > 0 ? ` + ${related.length} related` : ""}`,
        isCriticalPath: isCritical || streamLicenses.some(l => criticalIds.includes(l.license_type_id)),
        tasks: streamLicenses.map(l => ({
          licenseId: l.license_type_id,
          name: l.license_name,
          authority: l.issuing_authority,
          status: l.status,
          durationMonths: l.durationMonths,
          isBlocked: isBlocked(l),
          isUnconfirmed: l.applicability === "applicable_unconfirmed",
          unconfirmedReason: l.applicability_reason,
          fieldsComplete: l.fields_complete || 0,
          fieldsTotal: l.fields_total || 0,
          docsComplete: l.docs_complete || 0,
          docsTotal: l.docs_total || 0,
          notCorpusVerified: l.verification_class !== "pipeline",
          addedByUpdate: recentlyAdded.has(l.license_type_id) ? (appliedDate ?? null) : null,
        })),
      });
    }


    return {
      label: pc.label,
      description: pc.desc,
      monthRange: "",
      streams,
      unlockText: pc.unlock,
    } satisfies Phase;
  }).filter(Boolean) as Phase[];

  return phases;
}

function getCriticalPathInsight(phases: Phase[]): string {
  const criticalStreams = phases.flatMap(p => p.streams.filter(s => s.isCriticalPath));
  const blockedCritical = criticalStreams.flatMap(s => s.tasks.filter(t => t.isBlocked));

  if (blockedCritical.length > 0) {
    return `${blockedCritical[0]!.name} is on the critical path but blocked. Unblock its prerequisites first to avoid timeline delays.`;
  }

  const incompleteCritical = criticalStreams.flatMap(s => s.tasks.filter(t => t.status !== "approved"));
  if (incompleteCritical.length > 0) {
    return `Focus on ${incompleteCritical[0]!.name} — it's on the critical path with ~${incompleteCritical[0]!.durationMonths} months processing time. Starting early here saves the most time overall.`;
  }

  return "All critical path items are on track. Continue with parallel streams to maintain your timeline.";
}

import { tryParseJSON } from "../../lib/utils.ts";
import { partitionByPhase, UNPHASED_LABEL } from "../../lib/workplan-phases.ts";
